import { translator } from "../../translation/translator";

export interface UsersAdminUser {
    user_id?: number;
    display_name?: unknown;
    nick_name?: unknown;
    full_name?: unknown;
    first_name?: unknown;
    last_name?: unknown;
    email?: unknown;
    date_of_birth?: unknown;
    gender?: unknown;
    role?: unknown;
    user_status?: unknown;
    user_note?: unknown;
    avatar_url?: unknown;
    point?: unknown;
    posts_count?: unknown;
    review_count?: unknown;
    followers_count?: unknown;
    followings_count?: unknown;
    favorites_count?: unknown;
    updated_at?: unknown;
    last_login_at?: unknown;
    [key: string]: unknown;
}

export interface UsersAdminLoginHistoryItem {
    login_history_id?: unknown;
    user_id?: number;
    ip_address?: unknown;
    user_agent?: unknown;
    device_name?: unknown;
    login_method?: unknown;
    is_successful?: unknown;
    failure_reason?: unknown;
    created_at?: unknown;
    updated_at?: unknown;
    [key: string]: unknown;
}

export interface UsersAdminAgeRange {
    low: number;
    high: number;
}

export class UsersAdminUtils {
    public static ROLES = {
        ROOT: "users-admin-root",
        DATA: "users-admin-data",
        STATUS: "users-admin-status",
        COUNT: "users-admin-count",
        SEARCH: "users-admin-search",
        ROLE_FILTER: "users-admin-role-filter",
        STATUS_FILTER: "users-admin-status-filter",
        INCLUDE_DUMMY_FILTER: "users-admin-include-dummy-filter",
        LIST: "users-admin-list",
        EDIT: "users-admin-edit",
        HISTORY: "users-admin-history",
        HISTORY_MODAL: "users-admin-history-modal",
        HISTORY_MODAL_OVERLAY: "users-admin-history-modal-overlay",
        HISTORY_MODAL_CLOSE: "close-users-admin-history-modal",
        HISTORY_MODAL_TITLE: "users-admin-history-modal-title",
        HISTORY_MODAL_SPINNER: "users-admin-history-modal-spinner",
        HISTORY_MODAL_STATUS: "users-admin-history-modal-status",
        HISTORY_MODAL_BODY: "users-admin-history-modal-body",
        CREATE_MODAL: "users-admin-create-modal",
        CREATE_MODAL_OVERLAY: "users-admin-create-modal-overlay",
        CREATE_MODAL_CLOSE: "close-users-admin-create-modal",
        CREATE_FORM: "users-admin-create-form",
        CREATE_EMAIL: "users-admin-create-email",
        CREATE_NICKNAME: "users-admin-create-nickname",
        CREATE_SUBMIT: "users-admin-create-submit",
        CREATE_RESULT: "users-admin-create-result",
        CREATE_RESULT_PASSWORD: "users-admin-create-result-password",
        CREATE_RESULT_EMAIL: "users-admin-create-result-email",
        CREATE_RESULT_NICKNAME: "users-admin-create-result-nickname",
        SAVE: "users-admin-save",
        DELETE: "users-admin-delete",
    };

    public static ROLE_OPTIONS = ["admin", "editor", "moderator", "expert", "user"];

    public static STATUS_OPTIONS = ["active", "inactive", "dummy", "migrated"];

    public static toText(value: unknown): string {
        return typeof value === "string" || typeof value === "number" ? String(value) : "";
    }

    public static toUserId(value: unknown): number | null {
        const parsed = Number(this.toText(value).trim());
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    }

    public static toUsersArray(value: unknown): UsersAdminUser[] {
        return Array.isArray(value)
            ? value
                  .filter((item): item is UsersAdminUser => !!item && typeof item === "object")
                  .map((item) => {
                      const userId = this.toUserId(item.user_id);
                      return userId === null ? item : { ...item, user_id: userId };
                  })
            : [];
    }

    public static toLoginHistoryArray(value: unknown): UsersAdminLoginHistoryItem[] {
        return Array.isArray(value)
            ? value
                  .filter((item): item is UsersAdminLoginHistoryItem => !!item && typeof item === "object")
                  .map((item) => {
                      const userId = this.toUserId(item.user_id);
                      return userId === null ? item : { ...item, user_id: userId };
                  })
            : [];
    }

    public static normalizeRole(value: unknown): string {
        return this.toText(value).trim().toLowerCase();
    }

    public static normalizeStatus(value: unknown): string {
        return this.toText(value).trim().toLowerCase();
    }

    public static normalizeGender(value: unknown): string {
        return this.toText(value).trim().toLowerCase();
    }

    public static toBoolean(value: unknown): boolean {
        if (typeof value === "boolean") {
            return value;
        }

        const normalized = this.toText(value).trim().toLowerCase();
        return normalized === "1" || normalized === "true" || normalized === "yes";
    }

    public static formatDateTime(value: unknown): string {
        const raw = this.toText(value).trim();
        if (!raw) return "-";

        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) {
            return raw;
        }

        const yyyy = parsed.getFullYear();
        const mm = String(parsed.getMonth() + 1).padStart(2, "0");
        const dd = String(parsed.getDate()).padStart(2, "0");
        const hh = String(parsed.getHours()).padStart(2, "0");
        const mi = String(parsed.getMinutes()).padStart(2, "0");

        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    }

    public static buildSearchTarget(user: UsersAdminUser): string {
        return [
            this.toText(user.display_name),
            this.toText(user.nick_name),
            this.toText(user.full_name),
            this.toText(user.first_name),
            this.toText(user.last_name),
            this.toText(user.email),
        ]
            .join(" ")
            .toLowerCase();
    }

    public static filterUsers(
        users: UsersAdminUser[],
        filters: { search?: unknown; role?: unknown; userStatus?: unknown },
    ): UsersAdminUser[] {
        const search = this.toText(filters.search).trim().toLowerCase();
        const role = this.normalizeRole(filters.role);
        const userStatus = this.normalizeStatus(filters.userStatus);

        return users.filter((user) => {
            if (role && this.normalizeRole(user.role) !== role) {
                return false;
            }
            if (userStatus && this.normalizeStatus(user.user_status) !== userStatus) {
                return false;
            }
            if (search && !this.buildSearchTarget(user).includes(search)) {
                return false;
            }

            return true;
        });
    }

    public static sortUsersById(users: UsersAdminUser[]): UsersAdminUser[] {
        return [...users].sort((left, right) => {
            const leftId = this.toUserId(left.user_id);
            const rightId = this.toUserId(right.user_id);

            if (leftId !== null && rightId !== null) {
                return leftId - rightId;
            }

            return this.toText(left.user_id).localeCompare(this.toText(right.user_id), undefined, {
                numeric: true,
                sensitivity: "base",
            });
        });
    }

    public static roleLabel(role: unknown): string {
        const normalized = this.normalizeRole(role);
        return translator.translate(`role_${normalized}`) || this.toText(role) || "-";
    }

    public static userStatusLabel(userStatus: unknown): string {
        const normalized = this.normalizeStatus(userStatus);
        return translator.translate(`status_${normalized}`) || this.toText(userStatus) || "-";
    }

    public static loginFailureReasonLabel(failureReason: unknown): string {
        const normalized = this.toText(failureReason).trim().toLowerCase();
        if (!normalized) {
            return "-";
        }

        return translator.translate(`users_admin_login_history_reason_${normalized}`) || this.toText(failureReason);
    }

    public static nameNotRegisteredTranslateKey(): string {
        return "users_admin_name_not_registered";
    }

    public static nameNotRegisteredLabel(): string {
        return translator.translate(this.nameNotRegisteredTranslateKey()) || "Name not provided";
    }

    public static genderTranslateKey(gender: unknown): string {
        const normalized = this.normalizeGender(gender);
        return normalized ? `gender_${normalized}` : "";
    }

    public static genderIconClass(gender: unknown): string {
        const normalized = this.normalizeGender(gender);

        if (normalized === "male" || normalized === "female") {
            return "bi-person";
        }

        return "";
    }

    public static demographicsBadgeClass(gender: unknown): string {
        const normalized = this.normalizeGender(gender);

        if (normalized === "male") {
            return "user-demographics-badge user-demographics-badge--male";
        }

        if (normalized === "female") {
            return "user-demographics-badge user-demographics-badge--female";
        }

        return "user-demographics-badge user-demographics-badge--neutral";
    }

    public static ageRange(dateOfBirth: unknown): UsersAdminAgeRange | null {
        const raw = this.toText(dateOfBirth).trim();
        if (raw === "") {
            return null;
        }

        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }

        const now = new Date();
        let age = now.getFullYear() - parsed.getFullYear();
        const monthDiff = now.getMonth() - parsed.getMonth();
        const dayDiff = now.getDate() - parsed.getDate();
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age -= 1;
        }

        if (age <= 0) {
            return null;
        }

        const low = Math.floor(age / 5) * 5;
        const high = low + 4;

        return { low, high };
    }

    public static buildUpdatePayload(
        user: UsersAdminUser,
        role: unknown,
        userStatus: unknown,
        userNote: unknown,
    ): Record<string, string> {
        return {
            email: this.toText(user.email),
            role: this.normalizeRole(role),
            user_status: this.normalizeStatus(userStatus),
            user_note: this.toText(userNote),
        };
    }
}
